import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useFormTracking } from '@/shared/hooks/useFormTracking';
import { getApexCountryCode, getDefaultCountryCode } from '@/shared/localization';
import type { OAuthIdentity } from './oauth';
import type { ServerErrors } from '@/shared/utils/fieldErrors';
import PhoneField from '../common/fields/PhoneField';
import AuthFormHeading from '../common/components/AuthFormHeading';
import AuthSwitchLink from '../common/components/AuthSwitchLink';
import FormBaseError from '../common/components/FormBaseError';
import NameField from '../common/fields/NameField';
import CountryField from '../common/fields/CountryField';
import TermsCheckbox from '../common/fields/TermsCheckbox';
import SubmitButton from '../common/fields/SubmitButton';

export interface OAuthProfileFormValues {
  name: string;
  country: string;
  phone: string;
  termsAccepted: boolean;
}

interface Props {
  oauthIdentity: OAuthIdentity;
  serverErrors: ServerErrors;
  isLoading: boolean;
  onSubmit: (values: OAuthProfileFormValues) => void;
  onClearOAuth: () => void;
  onSwitchToSignIn: () => void;
}

export default function OAuthProfileForm({
  oauthIdentity,
  serverErrors,
  isLoading,
  onSubmit,
  onClearOAuth,
  onSwitchToSignIn,
}: Props) {
  const countryFromDomain = getApexCountryCode();
  const defaultCountry = countryFromDomain ?? getDefaultCountryCode() ?? '';
  const {
    register,
    control,
    trigger,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<OAuthProfileFormValues>({
    mode: 'onTouched',
    defaultValues: {
      name: oauthIdentity.name,
      country: defaultCountry,
      phone: '',
      termsAccepted: false,
    },
  });

  const { formProps, wrapSubmit, onInvalid } = useFormTracking<OAuthProfileFormValues>({
    formType: 'oauth_profile',
    clarityStage: 'oauth_profile',
    getValues,
    errors,
  });

  return (
    <div className="space-y-6">
      <AuthFormHeading headingId="auth.oauthProfile.heading" />

      <FormBaseError messages={serverErrors.base} />

      <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
            <svg className="h-5 w-5 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              <FormattedMessage
                id="auth.oauthProfile.signedInAs"
                values={{ name: oauthIdentity.name || oauthIdentity.email }}
              />
            </p>
            <p className="text-xs text-slate-500">{oauthIdentity.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClearOAuth}
          className="mt-3 text-xs font-semibold text-brand-700 hover:text-brand-600"
        >
          <FormattedMessage id="auth.oauthProfile.useDifferentAccount" />
        </button>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit(wrapSubmit(onSubmit), onInvalid)} {...formProps}>
        <NameField register={register} errors={errors} serverErrors={serverErrors} />

        <CountryField
          register={register}
          errors={errors}
          serverErrors={serverErrors}
          answeredByDomain={Boolean(countryFromDomain)}
          defaultCountry={defaultCountry}
        />

        <PhoneField control={control} trigger={trigger} errors={errors} serverErrors={serverErrors} />

        <TermsCheckbox register={register} errors={errors} serverErrors={serverErrors} />

        <SubmitButton isLoading={isLoading} submitId="auth.oauthProfile.submit" submittingId="auth.oauthProfile.submitting" />
      </form>

      <AuthSwitchLink labelId="auth.oauthProfile.switchToSignIn" onClick={onSwitchToSignIn} />
    </div>
  );
}
